// Standard shop math and inspection formulas for ManuCalc calculator expansion.
// Formula comments identify the source type used in machining/inspection practice.

const INCH_TO_MM = 25.4;
const MM_TO_INCH = 1 / 25.4;

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const deg = (radians) => (radians * 180) / Math.PI;
const rad = (degrees) => (Number(degrees) * Math.PI) / 180;

export const formatDimension = (value, unit = "inch") =>
  `${n(value).toFixed(unit === "metric" ? 3 : 4)} ${unit === "metric" ? "mm" : "in"}`;

export const formatAngle = (value) => `${n(value).toFixed(3)} deg`;
export const formatCost = (value) => `$${n(value).toFixed(2)}`;
export const unitLabel = (unit) => (unit === "metric" ? "mm" : "in");

export const COMMON_SCREW_DATA = {
  inch: [
    { size: "#4", clearance: 0.1285, shcsHeadDia: 0.183, shcsHeadHeight: 0.112, buttonHeadDia: 0.213, buttonHeadHeight: 0.059, flatHeadDia: 0.225 },
    { size: "#6", clearance: 0.1495, shcsHeadDia: 0.226, shcsHeadHeight: 0.138, buttonHeadDia: 0.262, buttonHeadHeight: 0.073, flatHeadDia: 0.279 },
    { size: "#8", clearance: 0.1695, shcsHeadDia: 0.270, shcsHeadHeight: 0.164, buttonHeadDia: 0.312, buttonHeadHeight: 0.087, flatHeadDia: 0.332 },
    { size: "#10", clearance: 0.196, shcsHeadDia: 0.312, shcsHeadHeight: 0.190, buttonHeadDia: 0.361, buttonHeadHeight: 0.101, flatHeadDia: 0.385 },
    { size: "1/4", clearance: 0.257, shcsHeadDia: 0.375, shcsHeadHeight: 0.250, buttonHeadDia: 0.437, buttonHeadHeight: 0.132, flatHeadDia: 0.507 },
    { size: "5/16", clearance: 0.323, shcsHeadDia: 0.469, shcsHeadHeight: 0.312, buttonHeadDia: 0.547, buttonHeadHeight: 0.166, flatHeadDia: 0.635 },
    { size: "3/8", clearance: 0.386, shcsHeadDia: 0.562, shcsHeadHeight: 0.375, buttonHeadDia: 0.656, buttonHeadHeight: 0.199, flatHeadDia: 0.762 },
    { size: "1/2", clearance: 0.515, shcsHeadDia: 0.750, shcsHeadHeight: 0.500, buttonHeadDia: 0.875, buttonHeadHeight: 0.265, flatHeadDia: 1.016 },
  ],
  metric: [
    { size: "M3", clearance: 3.4, shcsHeadDia: 5.5, shcsHeadHeight: 3.0, buttonHeadDia: 5.7, buttonHeadHeight: 1.65, flatHeadDia: 6.0 },
    { size: "M4", clearance: 4.5, shcsHeadDia: 7.0, shcsHeadHeight: 4.0, buttonHeadDia: 7.6, buttonHeadHeight: 2.2, flatHeadDia: 8.0 },
    { size: "M5", clearance: 5.5, shcsHeadDia: 8.5, shcsHeadHeight: 5.0, buttonHeadDia: 9.5, buttonHeadHeight: 2.75, flatHeadDia: 10.0 },
    { size: "M6", clearance: 6.6, shcsHeadDia: 10.0, shcsHeadHeight: 6.0, buttonHeadDia: 10.5, buttonHeadHeight: 3.3, flatHeadDia: 12.0 },
    { size: "M8", clearance: 9.0, shcsHeadDia: 13.0, shcsHeadHeight: 8.0, buttonHeadDia: 14.0, buttonHeadHeight: 4.4, flatHeadDia: 16.0 },
    { size: "M10", clearance: 11.0, shcsHeadDia: 16.0, shcsHeadHeight: 10.0, buttonHeadDia: 17.5, buttonHeadHeight: 5.5, flatHeadDia: 20.0 },
    { size: "M12", clearance: 13.5, shcsHeadDia: 18.0, shcsHeadHeight: 12.0, buttonHeadDia: 21.0, buttonHeadHeight: 6.6, flatHeadDia: 24.0 },
  ],
};

export const MATERIAL_HP_DATA = [
  { id: "aluminum", name: "Aluminum alloys", unitHp: 0.30 },
  { id: "brass", name: "Brass / bronze", unitHp: 0.40 },
  { id: "mild-steel", name: "Mild steel", unitHp: 1.00 },
  { id: "cold-rolled-steel", name: "Cold rolled steel", unitHp: 1.00 },
  { id: "hot-rolled-steel", name: "Hot rolled steel", unitHp: 0.95 },
  { id: "alloy-steel", name: "Alloy steel", unitHp: 1.30 },
  { id: "stainless", name: "Stainless steel", unitHp: 1.50 },
  { id: "tool-steel", name: "Tool steel", unitHp: 1.70 },
  { id: "titanium", name: "Titanium alloys", unitHp: 1.20 },
  { id: "cast-iron", name: "Cast iron", unitHp: 0.80 },
];

export const convertToInchInputs = (values, unit, fields) => {
  if (unit !== "metric") return values;
  return fields.reduce((next, field) => {
    next[field] = n(values[field]) * MM_TO_INCH;
    return next;
  }, { ...values });
};

export const convertFromInch = (value, unit) => (unit === "metric" ? value * INCH_TO_MM : value);

export const calculateTaper = (input) => {
  const large = n(input.largeDiameter);
  const small = n(input.smallDiameter);
  const length = n(input.length);
  const centerDistance = n(input.centerDistance) || length;
  const startZ = Number.isFinite(Number(input.startZ)) ? n(input.startZ) : 0;
  const feedPerRev = n(input.feedPerRev);
  const errors = [];
  if (large <= 0 || small <= 0) errors.push("Diameters must be greater than zero.");
  if (large <= small) errors.push("Large diameter must be greater than small diameter.");
  if (length <= 0) errors.push("Taper length must be greater than zero.");
  if (centerDistance <= 0) errors.push("Distance between centers must be greater than zero.");
  if (feedPerRev < 0) errors.push("Feed per revolution cannot be negative.");
  if (errors.length) return { errors };

  // Standard shop taper geometry.
  const difference = large - small;
  const halfAngle = deg(Math.atan(difference / (2 * length)));
  const taperPerUnit = difference / length;
  const tailstockOffset = (difference * centerDistance) / (2 * length);
  const taperPerFoot = taperPerUnit * 12;
  const taperPer100mm = taperPerUnit * 100;
  const endZ = startZ - length;
  return {
    difference,
    taperPerUnit,
    includedAngle: halfAngle * 2,
    halfAngle,
    compoundAngle: halfAngle,
    compoundFromCrossSlide: 90 - halfAngle,
    taperAttachmentAngle: halfAngle,
    taperPerFoot,
    taperPer100mm,
    tailstockOffset,
    gcodeInputs: {
      operation: "Lathe OD taper turning",
      xStartDiameter: large,
      xEndDiameter: small,
      zStart: startZ,
      zEnd: endZ,
      length,
      feedPerRev,
      note: "Use diameter-mode X values for a typical lathe control. Confirm the G-code calculator machine/control selection before posting.",
      sampleMove: `G01 X${small.toFixed(4)} Z${endZ.toFixed(4)}${feedPerRev > 0 ? ` F${feedPerRev.toFixed(4)}` : ""}`,
    },
    formulas: [
      "Diameter difference = large diameter - small diameter",
      "Half angle = atan((large diameter - small diameter) / (2 x length))",
      "Included angle = half angle x 2",
      "Taper per unit = diameter difference / length",
      "Tailstock offset = (diameter difference x distance between centers) / (2 x taper length)",
    ],
  };
};

export const calculateDrillDepth = (input) => {
  const depth = n(input.fullDepth);
  const diameter = n(input.drillDiameter);
  const pointAngle = n(input.pointAngle || 118);
  const errors = [];
  if (depth < 0) errors.push("Full-diameter depth cannot be negative.");
  if (diameter <= 0) errors.push("Drill diameter must be greater than zero.");
  if (pointAngle <= 0 || pointAngle >= 180) errors.push("Point angle must be between 0 and 180 degrees.");
  if (errors.length) return { errors };

  // Standard drill point cone depth formula.
  const tipAllowance = (diameter / 2) / Math.tan(rad(pointAngle / 2));
  return {
    tipAllowance,
    totalDepth: depth + tipAllowance,
    formulas: [
      "Drill tip allowance = (drill diameter / 2) / tan(point angle / 2)",
      "Total programmed depth = full-diameter depth + drill tip allowance",
    ],
  };
};

const screwRecord = (unit, size) =>
  (COMMON_SCREW_DATA[unit] || COMMON_SCREW_DATA.inch).find((item) => item.size === size);

export const calculateCounterbore = (input) => {
  const unit = input.unit || "inch";
  const record = screwRecord(unit, input.screwSize);
  const clearance = n(input.clearance);
  const customHeadDiameter = n(input.customHeadDiameter);
  const customHeadHeight = n(input.customHeadHeight);
  const errors = [];
  let headDiameter = customHeadDiameter;
  let headHeight = customHeadHeight;
  let dataSource = "User-defined";

  if (input.screwType !== "custom" && record) {
    const prefix = input.screwType === "button" ? "button" : input.screwType === "flat" ? "flat" : "shcs";
    headDiameter = record[`${prefix}HeadDia`] || customHeadDiameter;
    headHeight = record[`${prefix}HeadHeight`] || customHeadHeight;
    dataSource = "Common fastener reference data";
  }

  if (headDiameter <= 0) errors.push("Head diameter is required for this screw type/size.");
  if (headHeight <= 0) errors.push("Head height is required for this screw type/size.");
  if (clearance < 0) errors.push("Clearance cannot be negative.");
  if (errors.length) return { errors };

  return {
    counterboreDiameter: headDiameter + clearance,
    counterboreDepth: headHeight + clearance,
    clearanceHole: record?.clearance || null,
    dataSource,
    formulas: [
      "Recommended counterbore diameter = head diameter + desired clearance",
      "Recommended counterbore depth = head height + desired clearance",
      "Clearance hole is shown only when reference data exists for the selected screw size.",
    ],
  };
};

export const calculateCountersink = (input) => {
  const topDiameter = n(input.topDiameter);
  const holeDiameter = n(input.holeDiameter);
  const angle = n(input.angle || 82);
  const errors = [];
  if (topDiameter <= 0 || holeDiameter <= 0) errors.push("Top diameter and hole diameter must be greater than zero.");
  if (topDiameter <= holeDiameter) errors.push("Top diameter must be larger than the hole diameter.");
  if (angle <= 0 || angle >= 180) errors.push("Countersink angle must be between 0 and 180 degrees.");
  if (errors.length) return { errors };

  // Standard countersink cone depth formula.
  const depth = (topDiameter - holeDiameter) / (2 * Math.tan(rad(angle / 2)));
  return {
    depth,
    toolMovementDepth: depth,
    formulas: [
      "Countersink depth = (top diameter - hole diameter) / (2 x tan(angle / 2))",
      "Tool movement depth equals calculated cone depth when measured along the tool axis.",
    ],
  };
};

export const calculateBoreInterpolation = (input) => {
  const bore = n(input.boreDiameter);
  const cutter = n(input.cutterDiameter);
  const allowance = n(input.finishAllowance);
  const measuredBore = n(input.measuredBore);
  const lowerTolerance = n(input.lowerTolerance);
  const upperTolerance = n(input.upperTolerance);
  const finishPasses = Math.max(1, Math.round(n(input.finishPasses) || 1));
  const errors = [];
  if (bore <= 0 || cutter <= 0) errors.push("Bore diameter and cutter diameter must be greater than zero.");
  if (cutter >= bore) errors.push("Cutter diameter must be smaller than desired bore diameter.");
  if (allowance < 0) errors.push("Finish allowance cannot be negative.");
  if (lowerTolerance > 0 && upperTolerance > 0 && lowerTolerance >= upperTolerance) {
    errors.push("Minimum tolerance limit must be smaller than maximum tolerance limit.");
  }
  if (errors.length) return { errors };

  // Standard circular interpolation centerline geometry.
  const finalToolpathDiameter = bore - cutter;
  const roughToolpathDiameter = finalToolpathDiameter - (2 * allowance);
  const measuredError = measuredBore > 0 ? bore - measuredBore : 0;
  const radialCompAdjustment = measuredBore > 0 ? measuredError / 2 : 0;
  const passFail = lowerTolerance > 0 && upperTolerance > 0 && measuredBore > 0
    ? (measuredBore >= lowerTolerance && measuredBore <= upperTolerance ? "Pass" : "Fail")
    : "Enter measured bore and tolerance limits";
  return {
    toolpathDiameter: roughToolpathDiameter,
    finalToolpathDiameter,
    toolpathRadius: roughToolpathDiameter / 2,
    finalToolpathRadius: finalToolpathDiameter / 2,
    radialStockAllowance: allowance,
    finishPassStock: allowance / finishPasses,
    measuredError,
    radialCompAdjustment,
    diameterCompAdjustment: measuredError,
    passFail,
    toleranceBand: lowerTolerance > 0 && upperTolerance > 0 ? upperTolerance - lowerTolerance : 0,
    formulas: [
      "Final toolpath diameter = desired bore diameter - cutter diameter",
      "Rough/finish toolpath diameter = final toolpath diameter - (2 x radial finish allowance)",
      "Toolpath radius = toolpath diameter / 2",
      "Measured diameter error = desired bore diameter - measured bore diameter",
      "Radial cutter comp adjustment = measured diameter error / 2",
    ],
  };
};

export const calculateKnurl = (input) => {
  const diameter = n(input.blankDiameter);
  const pitch = n(input.pitch);
  const unit = input.unit || "inch";
  const errors = [];
  if (diameter <= 0) errors.push("Blank diameter must be greater than zero.");
  if (pitch <= 0) errors.push(unit === "metric" ? "Knurl pitch must be greater than zero." : "Knurl TPI must be greater than zero.");
  if (errors.length) return { errors };

  // Approximate shop knurling tooth-count math.
  const circumference = Math.PI * diameter;
  const toothSpacing = unit === "metric" ? pitch : 1 / pitch;
  const toothCount = circumference / toothSpacing;
  const nearestToothCount = Math.max(1, Math.round(toothCount));
  return {
    circumference,
    toothSpacing,
    toothCount,
    nearestToothCount,
    adjustedDiameter: (nearestToothCount * toothSpacing) / Math.PI,
    formulas: [
      "Circumference = pi x blank diameter",
      unit === "metric" ? "Tooth spacing = metric knurl pitch" : "Tooth spacing = 1 / TPI",
      "Tooth count = circumference / tooth spacing",
      "Adjusted diameter = nearest whole tooth count x tooth spacing / pi",
    ],
  };
};

export const calculateTruePosition = (input) => {
  const xDev = n(input.actualX) - n(input.nominalX);
  const yDev = n(input.actualY) - n(input.nominalY);
  const tolerance = n(input.tolerance);
  const actualSize = n(input.actualFeatureSize);
  const mmcSize = n(input.mmcSize);
  const isHole = input.featureType !== "pin";
  const appliesBonus = input.materialCondition === "MMC" && actualSize > 0 && mmcSize > 0;
  const bonusTolerance = appliesBonus ? Math.max(0, isHole ? actualSize - mmcSize : mmcSize - actualSize) : 0;
  const totalTolerance = tolerance + bonusTolerance;
  // GD&T diametrical true position calculation.
  const radialError = Math.sqrt((xDev ** 2) + (yDev ** 2));
  const truePositionError = 2 * radialError;
  const remainingTolerance = totalTolerance > 0 ? totalTolerance - truePositionError : 0;
  const toleranceUsed = totalTolerance > 0 ? (truePositionError / totalTolerance) * 100 : 0;
  return {
    xDeviation: xDev,
    yDeviation: yDev,
    radialError,
    truePositionError,
    baseTolerance: tolerance,
    bonusTolerance,
    totalTolerance,
    remainingTolerance,
    toleranceUsed,
    correctionX: -xDev,
    correctionY: -yDev,
    passFail: totalTolerance > 0 ? (truePositionError <= totalTolerance ? "Pass" : "Fail") : "No tolerance entered",
    datumReference: [input.datumA, input.datumB, input.datumC].filter(Boolean).join(" | ") || "No datum reference entered",
    materialCondition: input.materialCondition || "RFS",
    formulas: [
      "X deviation = actual X - nominal X",
      "Y deviation = actual Y - nominal Y",
      "Radial error = sqrt(X deviation^2 + Y deviation^2)",
      "True position diameter error = 2 x radial error",
      "MMC bonus tolerance = actual size - MMC size for holes, or MMC size - actual size for pins",
      "Total positional tolerance = stated tolerance + bonus tolerance",
    ],
  };
};

export const calculateHorsepower = (input) => {
  const unit = input.unit || "inch";
  const material = MATERIAL_HP_DATA.find((item) => item.id === input.material) || MATERIAL_HP_DATA[2];
  const width = unit === "metric" ? n(input.widthOfCut) * MM_TO_INCH : n(input.widthOfCut);
  const depth = unit === "metric" ? n(input.depthOfCut) * MM_TO_INCH : n(input.depthOfCut);
  const feed = unit === "metric" ? n(input.feedrate) * MM_TO_INCH : n(input.feedrate);
  const directMrr = unit === "metric" ? n(input.materialRemovalRate) * 0.0000610237 : n(input.materialRemovalRate);
  const efficiency = n(input.efficiency || 80) / 100;
  const unitHp = n(input.unitHorsepower) || material.unitHp;
  const errors = [];
  if (efficiency <= 0 || efficiency > 1) errors.push("Machine efficiency must be greater than 0 and no more than 100%.");
  if (unitHp <= 0) errors.push("Unit horsepower must be greater than zero.");
  const mrr = directMrr > 0 ? directMrr : width * depth * feed;
  if (mrr <= 0) errors.push("Enter material removal rate, or width/depth/feedrate values greater than zero.");
  if (errors.length) return { errors };

  // Standard machining horsepower estimate from MRR and unit horsepower.
  const cuttingHp = mrr * unitHp;
  return {
    materialLabel: material.name,
    mrrIn3Min: mrr,
    mrrDisplay: unit === "metric" ? mrr / 0.0000610237 : mrr,
    cuttingHp,
    spindleHp: cuttingHp / efficiency,
    unitHp,
    formulas: [
      unit === "metric"
        ? "MRR = width of cut x depth of cut x feedrate, entered/displayed as mm^3/min and converted to in^3/min for unit horsepower"
        : "MRR = width of cut x depth of cut x feedrate",
      "Cutting horsepower = MRR x unit horsepower",
      "Required spindle horsepower = cutting horsepower / machine efficiency",
    ],
  };
};
